import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, Send, Square, X } from 'lucide-react'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { apiErrorText } from '@/lib/api-error'
import { assistentApi, type AssistentKontext, type AssistentNachricht, type AssistentVorschlag, type AssistentWerkzeugInfo, type Gespraech as GespraechDaten } from '@/lib/assistent/assistent.api'
import { useAssistent } from '@/lib/assistent/assistent.store'
import { beilageFuer } from '@/lib/assistent/assistent-beilagen'
import { Antworttext } from './Antworttext'
import { Vorschlagsliste } from './Vorschlagsliste'

/**
 * AP-18 5.1 — **ein Gespräch mit dem Assistenten**: Verlauf, Zwischenstand, Eingabe. Dieselbe
 * Fläche in der Seitenleiste und unter dem Menüpunkt „Assistent".
 *
 * Die Antwort entsteht auf dem Server im Hintergrund (AS-10); solange sie läuft, fragt die Fläche
 * jede Sekunde nach und zeigt, welches Werkzeug gerade arbeitet. Ein neues Gespräch entsteht erst
 * mit seiner ersten Frage — vorher gibt es nichts zu speichern.
 */
export function Gespraech({
  gespraechId,
  onGespraech,
  kontext,
  vorgabe,
  onVorgabeVerbraucht,
  onGeaendert,
}: {
  /** `null` = neues Gespräch */
  gespraechId: string | null
  onGespraech: (id: string) => void
  /** wo der Benutzer ist — geht mit jeder Frage mit (Spezifikation 6.) */
  kontext?: AssistentKontext
  /** Frage, die gleich gestellt wird (Eingabefeld auf einem Dashboard) */
  vorgabe?: string | null
  onVorgabeVerbraucht?: () => void
  /** ein Gespräch ist entstanden oder hat eine neue Nachricht — für die Liste daneben */
  onGeaendert?: () => void
}) {
  const { t } = useTranslation()
  const stand = useAssistent((s) => s.stand)
  const ladeStand = useAssistent((s) => s.ladeStand)
  const [gespraech, setGespraech] = useState<GespraechDaten | null>(null)
  const [text, setText] = useState('')
  // Der Ersatztext entsteht erst beim Anzeigen: Ein Ladeeffekt, der am Übersetzer hinge, lüde bei jedem Sprachwechsel neu.
  const [fehler, setFehler] = useState<{ text: string; ersatz: 'loadError' | 'askError' | 'stopError' } | null>(null)
  const [sendet, setSendet] = useState(false)
  const unten = useRef<HTMLDivElement>(null)
  const eingabe = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    void ladeStand()
  }, [ladeStand])

  // Gespräch laden, wenn ein anderes gewählt wird; das eben selbst angelegte ist schon da.
  useEffect(() => {
    setFehler(null)
    if (!gespraechId) {
      setGespraech(null)
      return
    }
    let weg = false
    setGespraech((alt) => (alt?.id === gespraechId ? alt : null))
    void assistentApi
      .lesen(gespraechId)
      .then((g) => !weg && setGespraech(g))
      .catch((e) => !weg && setFehler({ text: apiErrorText(e, ''), ersatz: 'loadError' }))
    return () => {
      weg = true
    }
  }, [gespraechId])

  // Solange die Antwort entsteht, jede Sekunde nachfragen.
  const laeuft = !!gespraech?.running
  const laufendeId = laeuft ? gespraech!.id : null
  useEffect(() => {
    if (!laufendeId) return
    const uhr = setInterval(() => {
      void assistentApi
        .lesen(laufendeId)
        .then((g) => {
          setGespraech((alt) => (alt?.id === g.id ? g : alt))
          if (!g.running) {
            onGeaendert?.()
            // Der Verbrauch hat sich bewegt — der Hinweis zum Kontingent soll stimmen.
            void ladeStand()
          }
        })
        .catch(() => undefined)
    }, 1000)
    return () => clearInterval(uhr)
  }, [laufendeId, onGeaendert, ladeStand])

  const anzahl = gespraech?.messages.length ?? 0
  const schritte = gespraech?.messages.at(-1)?.steps?.length ?? 0
  useEffect(() => {
    unten.current?.scrollIntoView({ block: 'end' })
  }, [anzahl, laeuft, schritte])

  const senden = useCallback(
    async (roh: string) => {
      const frage = roh.trim()
      if (!frage || sendet || laeuft) return false
      setSendet(true)
      setFehler(null)
      try {
        const g = gespraechId ? await assistentApi.fragen(gespraechId, frage, kontext) : await assistentApi.beginnen(frage, kontext)
        setGespraech(g)
        if (!gespraechId) onGespraech(g.id)
        onGeaendert?.()
        return true
      } catch (e) {
        setFehler({ text: apiErrorText(e, ''), ersatz: 'askError' })
        // Vielleicht wurde der Assistent inzwischen ausgeschaltet oder das Kontingent ist erreicht.
        void ladeStand()
        return false
      } finally {
        setSendet(false)
      }
    },
    [gespraechId, kontext, sendet, laeuft, onGespraech, onGeaendert, ladeStand],
  )

  // Frage vom Dashboard: einmal stellen, sobald der Assistent bereit ist.
  const gestellt = useRef<string | null>(null)
  useEffect(() => {
    if (!vorgabe || gestellt.current === vorgabe || !stand?.ready) return
    gestellt.current = vorgabe
    onVorgabeVerbraucht?.()
    void senden(vorgabe)
  }, [vorgabe, stand?.ready, senden, onVorgabeVerbraucht])

  async function abschicken() {
    if (await senden(text)) setText('')
    eingabe.current?.focus()
  }

  async function abbrechen() {
    if (!gespraech) return
    try {
      setGespraech(await assistentApi.stoppen(gespraech.id))
    } catch (e) {
      setFehler({ text: apiErrorText(e, ''), ersatz: 'stopError' })
    }
  }

  // Ein Vorschlag wurde übernommen oder verworfen: sein neuer Stand ersetzt den alten in der Antwort.
  const vorschlagGeaendert = useCallback((v: AssistentVorschlag) => {
    setGespraech((alt) => (alt ? { ...alt, messages: alt.messages.map((m) => (m.proposals?.some((x) => x.id === v.id) ? { ...m, proposals: m.proposals.map((x) => (x.id === v.id ? v : x)) } : m)) } : alt))
  }, [])

  const gesperrt = stand && !stand.ready ? stand.blocked : null
  const prozent = stand?.quota && stand.quota.limit > 0 ? Math.min(100, Math.round((stand.quota.used / stand.quota.limit) * 100)) : null

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm">
        {anzahl === 0 && !gesperrt && <Einstieg bereich={kontext?.bereich} art={kontext?.art} />}
        {gespraech?.messages.map((m) => <Nachricht key={m.id} nachricht={m} onWeiter={() => void senden(t('assistant.continueText'))} onVorschlag={vorschlagGeaendert} letzte={m === gespraech.messages.at(-1)} />)}
        <div ref={unten} />
      </div>

      {gesperrt ? (
        <div className="border-t border-border px-4 py-3 text-sm text-muted-foreground" role="status">
          {t(`assistant.blocked.${gesperrt}`)}{' '}
          {gesperrt === 'AI_DISABLED' ? null : stand?.canConfigure ? (
            <Link to="/settings/ai" className="text-primary hover:underline">
              {t(gesperrt === 'OFF' ? 'assistant.configureOn' : 'assistant.configure')}
            </Link>
          ) : (
            t('assistant.configureOther')
          )}
        </div>
      ) : (
        <div className="border-t border-border px-4 py-3">
          {fehler && <p className="mb-2 text-sm text-destructive">{fehler.text || t(`assistant.${fehler.ersatz}`)}</p>}
          {stand?.quota?.warn && prozent !== null && <p className="mb-2 text-xs text-amber-700 dark:text-amber-300">{t('assistant.quotaWarn', { percent: prozent })}</p>}
          {stand?.daily?.warn && stand.daily.limit > 0 && (
            <p className="mb-2 text-xs text-amber-700 dark:text-amber-300">{t('assistant.dailyWarn', { percent: Math.min(100, Math.round((stand.daily.used / stand.daily.limit) * 100)) })}</p>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={eingabe}
              className="min-h-[2.5rem] flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
              rows={Math.min(6, Math.max(1, text.split('\n').length))}
              placeholder={t('assistant.placeholder')}
              aria-label={t('assistant.placeholder')}
              value={text}
              maxLength={4000}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault()
                  void abschicken()
                }
              }}
            />
            {laeuft ? (
              <button type="button" className="inline-flex h-10 items-center gap-1 rounded-md border border-border px-3 text-sm hover:bg-accent" onClick={() => void abbrechen()}>
                <Square className="h-3.5 w-3.5" /> {t('assistant.stop')}
              </button>
            ) : (
              <button
                type="button"
                className="inline-flex h-10 items-center gap-1 rounded-md bg-primary px-3 text-sm text-primary-foreground disabled:opacity-50"
                disabled={sendet || !text.trim()}
                onClick={() => void abschicken()}
              >
                <Send className="h-4 w-4" /> {t('assistant.ask')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Nachricht({ nachricht: m, letzte, onWeiter, onVorschlag }: { nachricht: AssistentNachricht; letzte: boolean; onWeiter: () => void; onVorschlag: (v: AssistentVorschlag) => void }) {
  const { t } = useTranslation()
  if (m.role === 'USER') {
    return <div className="ml-8 whitespace-pre-wrap rounded-lg bg-primary/10 px-3 py-2">{m.text}</div>
  }
  const schritte = m.steps ?? []
  return (
    <div className="mr-4 space-y-2">
      {m.status === 'RUNNING' && (
        <div className="space-y-1 text-muted-foreground">
          {schritte.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              {s.state === 'RUNNING' ? <KiArbeitet text={s.title} /> : s.state === 'DONE' ? <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden /> : <X className="h-3.5 w-3.5 text-destructive" aria-hidden />}
              {s.state !== 'RUNNING' && <span>{s.title}</span>}
            </div>
          ))}
          {!schritte.some((s) => s.state === 'RUNNING') && <KiArbeitet text={t('assistant.thinking')} />}
        </div>
      )}
      {m.text && (
        <div className="whitespace-pre-wrap leading-relaxed">
          <Antworttext text={m.text} />
        </div>
      )}
      {/* Vorschläge der Antwort (7.4) — auch unter einer abgebrochenen: Was vorgeschlagen ist, bleibt übernehmbar. */}
      {m.status !== 'RUNNING' && m.proposals?.map((v) => <Vorschlagsliste key={v.id} vorschlag={v} onGeaendert={onVorschlag} />)}
      {/* Was ein Fachmodul unter die Antwort gelegt hat (7.5): Es zeichnet selbst — eine Art, die niemand kennt, bleibt weg. */}
      {m.status !== 'RUNNING' &&
        m.attachments?.map((b, i) => {
          const Beilage = beilageFuer(b.art)
          return Beilage ? <Beilage key={`${b.art}-${i}`} daten={b.daten} /> : null
        })}
      {m.status === 'FAILED' && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{m.error || t('assistant.failed')}</p>}
      {m.status === 'STOPPED' && <p className="text-xs text-muted-foreground">{t('assistant.stopped')}</p>}
      {m.status === 'DONE' && schritte.length > 0 && <div className="text-[11px] text-muted-foreground">{[...new Set(schritte.map((s) => s.title))].join(' · ')}</div>}
      {m.status === 'DONE' && m.atLimit && letzte && (
        <button type="button" className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-accent" onClick={onWeiter}>
          {t('assistant.continue')}
        </button>
      )}
    </div>
  )
}

/** Leeres Gespräch: wofür der Assistent da ist und — auf Wunsch — was er für diesen Benutzer kann (5.1). */
function Einstieg({ bereich, art }: { bereich?: string; art?: string }) {
  const { t } = useTranslation()
  const [werkzeuge, setWerkzeuge] = useState<AssistentWerkzeugInfo[] | null>(null)
  const [offen, setOffen] = useState(false)
  useEffect(() => {
    if (!offen) return
    let weg = false
    void assistentApi
      .werkzeuge(bereich, art)
      .then((w) => !weg && setWerkzeuge(w))
      .catch(() => !weg && setWerkzeuge([]))
    return () => {
      weg = true
    }
  }, [offen, bereich, art])
  return (
    <div className="space-y-2 text-muted-foreground">
      <p>{t('assistant.intro')}</p>
      <button type="button" className="text-xs text-primary hover:underline" aria-expanded={offen} onClick={() => setOffen((o) => !o)}>
        {t('assistant.whatCan')}
      </button>
      {offen &&
        (werkzeuge === null ? (
          // Kein Aufruf an die KI — also keine springenden Punkte (KA-52).
          <p className="text-xs">{t('common.loading')}</p>
        ) : werkzeuge.length === 0 ? (
          <p className="text-xs">{t('assistant.whatCanEmpty')}</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {werkzeuge.map((w) => (
              <li key={w.name}>
                <span className="font-medium text-foreground">{w.title}</span>
                <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px]">{t(`assistant.kinds.${w.kind}`)}</span>
              </li>
            ))}
          </ul>
        ))}
    </div>
  )
}
